import React, { useState, useEffect } from 'react';
import { formatDateTime } from '../shared/timezone';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * CallHistoryPage - Shows all calls with transcripts and search
 */
export function CallHistoryPage({ highlightSessionId, onHighlightHandled }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedCall, setSelectedCall] = useState(null);
  const [loadingTranscript, setLoadingTranscript] = useState(null);
  const [error, setError] = useState('');

  // Fetch calls
  const fetchCalls = async (search = '') => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('limit', '100');
      
      const response = await fetch(`${API_URL}/api/transcripts?${params}`, {
        headers: {
          ...getAuthHeaders()
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Nicht authentifiziert. Bitte erneut einloggen.');
        }
        throw new Error('Fehler beim Laden der Anrufe');
      }
      
      const data = await response.json();
      setCalls(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalls();
  }, []);

  // Auto-expand highlighted session from dashboard click
  useEffect(() => {
    if (highlightSessionId && calls.length > 0 && !loading) {
      const targetCall = calls.find(c => c.session_id === highlightSessionId);
      if (targetCall) {
        loadTranscript(targetCall);
        if (onHighlightHandled) onHighlightHandled();
        // Scroll to the call after a short delay
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="call-item-${highlightSessionId}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    }
  }, [highlightSessionId, calls, loading]); // eslint-disable-line

  // Load transcript on demand for ElevenLabs conversations
  const loadTranscript = async (call) => {
    if (selectedCall?.session_id === call.session_id) {
      setSelectedCall(null);
      return;
    }
    
    // If transcript already loaded, just show it
    if (call.transcript && call.transcript.length > 0) {
      setSelectedCall(call);
      return;
    }
    
    // For ElevenLabs conversations, fetch transcript on demand
    if (call.source === 'elevenlabs' && call.session_id) {
      setLoadingTranscript(call.session_id);
      try {
        const response = await fetch(`${API_URL}/api/elevenlabs/conversations/${call.session_id}`, {
          headers: { ...getAuthHeaders() }
        });
        if (response.ok) {
          const data = await response.json();
          const transcript = (data.transcript || []).map(entry => ({
            role: entry.role === 'agent' ? 'assistant' : entry.role || 'user',
            content: entry.message || '',
            timestamp: entry.time ? `${Math.floor(entry.time / 60)}:${String(Math.floor(entry.time % 60)).padStart(2, '0')}` : null,
          }));
          // Update phone number from detail if available
          const callerPhone = data.caller_phone || call.caller_number;
          const updatedCall = { ...call, transcript, caller_number: callerPhone };
          setCalls(prev => prev.map(c => c.session_id === call.session_id ? updatedCall : c));
          setSelectedCall(updatedCall);
        } else {
          setSelectedCall({ ...call, transcript: [] });
        }
      } catch {
        setSelectedCall({ ...call, transcript: [] });
      } finally {
        setLoadingTranscript(null);
      }
    } else {
      setSelectedCall(call);
    }
  };

  // Search handler
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    fetchCalls(searchInput);
  };

  // Clear search
  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    fetchCalls('');
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format date
  const formatDate = (dateStr) => {
    return formatDateTime(dateStr);
  };

  // Highlight search term in text
  const highlightText = (text, term) => {
    if (!term || !text) return text;
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => 
      regex.test(part) ? <mark key={i} className="bg-yellow-200 px-0.5 rounded">{part}</mark> : part
    );
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="call-history-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1" data-testid="call-history-title">Anruf-Historie</h1>
        <p className="text-sm text-gray-600">Alle Anrufe und Gesprächsverläufe</p>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-2 sm:gap-3">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Transkripte durchsuchen..."
              className="w-full px-4 py-2.5 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              data-testid="transcript-search-input"
            />
            <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button
            type="submit"
            className="px-4 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors text-sm"
            data-testid="transcript-search-btn"
          >
            Suchen
          </button>
          {searchTerm && (
            <button
              type="button"
              onClick={clearSearch}
              className="px-3 py-2.5 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors text-sm"
            >
              Zurücksetzen
            </button>
          )}
        </div>
        {searchTerm && (
          <p className="text-xs text-indigo-600 mt-2">
            Suche nach: &quot;{searchTerm}&quot; — {calls.length} Ergebnis(se)
          </p>
        )}
      </form>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" data-testid="call-history-error">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12" data-testid="call-history-loading">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Lade Anrufe...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && calls.length === 0 && !error && (
        <div className="text-center py-12 bg-gray-50 rounded-lg" data-testid="call-history-empty">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <h3 className="text-base font-medium text-gray-800 mb-1">
            {searchTerm ? 'Keine Ergebnisse' : 'Noch keine Anrufe'}
          </h3>
          <p className="text-sm text-gray-600">
            {searchTerm 
              ? `Keine Transkripte mit "${searchTerm}" gefunden.`
              : 'Sobald Anrufe stattfinden, erscheinen sie hier.'}
          </p>
        </div>
      )}

      {/* Calls List */}
      {!loading && calls.length > 0 && (
        <div className="space-y-2" data-testid="call-history-list">
          {calls.map((call) => (
            <div
              key={call.session_id}
              className={`bg-white border rounded-lg overflow-hidden transition-all ${
                selectedCall?.session_id === call.session_id 
                  ? 'border-indigo-400 ring-2 ring-indigo-100' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`call-item-${call.session_id}`}
            >
              {/* Call Header */}
              <div
                onClick={() => loadTranscript(call)}
                className="p-3 sm:p-4 cursor-pointer flex items-center justify-between"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {/* Source Icon */}
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    call.source === 'elevenlabs'
                      ? 'bg-indigo-100 text-indigo-600'
                      : call.call_direction === 'outbound' 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-blue-100 text-blue-600'
                  }`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {call.call_direction === 'outbound' 
                        ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      }
                    </svg>
                  </div>
                  
                  {/* Call Info */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-800 text-sm truncate" data-testid={`caller-number-${call.session_id}`}>
                        {call.caller_number && call.caller_number !== 'Unbekannt' ? (
                          <span className="flex items-center gap-1.5">
                            <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {call.caller_number}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">Unbekannte Nummer</span>
                        )}
                      </span>
                      {call.title && call.title !== 'Gespräch' && (
                        <span className="text-xs text-gray-500 truncate hidden sm:inline">— {call.title}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="truncate">{call.agent_name}</span>
                      <span>·</span>
                      <span>{formatDate(call.created_at)}</span>
                      <span>·</span>
                      <span>{formatDuration(call.duration_seconds)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Right side */}
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <span className="text-xs text-gray-400 hidden sm:inline">{call.transcript_count || 0} Nachr.</span>
                  {loadingTranscript === call.session_id ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 border-t-transparent"></div>
                  ) : (
                    <svg 
                      className={`w-4 h-4 text-gray-400 transition-transform ${
                        selectedCall?.session_id === call.session_id ? 'rotate-180' : ''
                      }`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>
              </div>

              {/* Transcript (Expanded) */}
              {selectedCall?.session_id === call.session_id && (
                <div className="border-t border-gray-100 bg-gray-50 p-4">
                  <h4 className="font-medium text-gray-700 mb-3 text-sm flex items-center gap-2">
                    Transkript
                    {call.call_successful === 'true' && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Erfolgreich</span>
                    )}
                  </h4>
                  
                  {selectedCall.transcript && selectedCall.transcript.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {selectedCall.transcript.map((entry, idx) => (
                        <div
                          key={idx}
                          className={`flex gap-2 ${
                            entry.role === 'assistant' ? '' : 'flex-row-reverse'
                          }`}
                        >
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs ${
                            entry.role === 'assistant' 
                              ? 'bg-indigo-100 text-indigo-600' 
                              : 'bg-blue-100 text-blue-600'
                          }`}>
                            {entry.role === 'assistant' ? 'KI' : 'A'}
                          </div>
                          <div className={`max-w-[80%] p-2.5 rounded-lg text-sm ${
                            entry.role === 'assistant'
                              ? 'bg-white border border-gray-200'
                              : 'bg-blue-600 text-white'
                          }`}>
                            <p>
                              {searchTerm ? highlightText(entry.content, searchTerm) : entry.content}
                            </p>
                            {entry.timestamp && (
                              <p className={`text-xs mt-1 ${
                                entry.role === 'assistant' ? 'text-gray-400' : 'text-blue-200'
                              }`}>
                                {entry.timestamp}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">Kein Transkript verfügbar.</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CallHistoryPage;
